export const enum BundledFeatureCategory {
  SPORTS = 'Sports',
  METRICS = 'Metrics',
}

export const enum OnboardingSagaStep {
  ACCOUNT_CREATION = 'account_creation',
  PAYMENT_PREPROCESSING = 'payment_preprocessing',
  CONTRACT_SIGNING = 'contract_signing',
  PAYMENT_PROCESSING = 'payment_processing',
  KYC = 'kyc',
}

const enum OnboardingSagaStatus {
  RUNNING = 'running',
}

export interface IOnboardingDiscount {
  type?: 'total' | 'percent';
  apply_for_days?: number;
  value?: number;
}

interface PlanPriceInterface {
  region: number;
  annual?: number;
  month?: number;
  quarter?: number;
}

interface IDiscountPrice {
  discount_price: number;
  month_price: number;
  period: number;
  total_plan_price: number;
}

export type IOnboardingDiscounts = Record<number, Array<IDiscountPrice>>;

interface SignupUser {
  email: string;
  first_name?: string;
  last_name?: string;
  username?: string;
}

interface SignupPayment {
  client_secret: string;
  intent_type: 'setup' | 'payment';
}

export interface SignupPlanBillingPeriodDetail {
  id: number;
  period_type: number;
  renewal_unit: number;
  cancel_notice_time?: number;
  minimum_contract_duration?: number;
  renewal?: number;
}

export interface SignupPlan {
  id: number;
  name: string;
  description?: string;
  features?: string[];
  plan_prices?: Array<PlanPriceInterface>;
  discount_prices?: Record<number, Array<IDiscountPrice>>;
  test_types?: Record<string, boolean | null | undefined>;
  sports?: Record<string, boolean | null | undefined>;
  cancel_notice_time?: number;
  minimum_contract_duration?: number;
  renewal_unit?: number;
  billing_period_details: Array<SignupPlanBillingPeriodDetail>;
  bundled_features?: Array<{ slug: string; name: string; category: string }>;
  bundled_courses?: Array<{ id: number; name: string }>;
}

export interface SignupRegistrationData {
  first_name: string;
  last_name: string;
  email: string;
  privacy_policy: boolean;
  terms: boolean;
}

export interface SignupSubscriptionData {
  billing_country?: number;
  vat_number?: string;
  vat_required: boolean;
  billing_period: number;
  use_lactate?: boolean;
  use_ppd?: boolean;
  email?: string;
}

export interface ContractLinkData {
  envelope_id: string;
  signing_url: string;
}

export interface VerifyContractData {
  guid: string;
  envelope_id?: string;
}

export interface SignupPaymentData {
  guid: string;
  email: string;
  payment_intent?: string;
  payment_method?: string;
  setup_future_usage?: string;
  amount?: number;
  currency?: string;
  status?: string;
  error_message?: string;
  error_code?: string;
}

export interface ISignupKYCData {
  organization_name: string;
  email: string;
  website?: string;
  phone?: string;
  address_street_name: string;
  address_street_number: string;
  address_zip: string;
  address_city: string;
  address_state?: string;
  address_country_id?: number;
  vat_number?: string;
  invoice_org_name?: string; // Same as org name by default
  invoice_address_street_name?: string;
  invoice_address_street_number?: string;
  invoice_address_zip?: string;
  invoice_address_city?: string;
  invoice_address_state?: string;
  invoice_country_id?: number;
  invoice_vat_number?: string;
  country_id: number; // This is the overall default one
  is_use_invoice_address: boolean; // Default false
  photo?: File | null; // ArrayBuffer/string
}

export interface OnboardingCountryInterface {
  id: number;
  name: string;
  region: number;
  country_code: string;
  currency: string;
}

export interface OnboardingLinkPostData {
  plan: number;
  user_email?: string;
  discount_value?: number;
  discount_type?: 'total' | 'percent';
  apply_for_days?: number;
  plan_start_date?: string;
  plan_start_date_local?: string;
  link_expiration_date?: string;
}

export interface OnboardingDocusign {
  envelope_id: string;
  signing_url: string;
}

export interface IOnboardingLink {
  url: string;
  expires_at: string;
  plan_start_date?: string;
  apply_for_days?: number;
  discount_type?: 'total' | 'percent';
  discount_value?: number;
}

export interface IOnboardingSubscriptionDetails {
  billing_country: number;
  billing_period: number;
  /** Two-letter country code */
  billing_country_code: string;
}

export interface IOnboardingSaga {
  account_type?: number; // deprecate
  current_step: OnboardingSagaStep;
  discount: IOnboardingDiscount; // deprecate
  expires_at: string; // deprecate
  guid: string;
  plan: SignupPlan;
  status: OnboardingSagaStatus;
  url: string; // deprecate
  user: SignupUser; // deprecate
  payment?: SignupPayment;
  docusign?: OnboardingDocusign; // deprecate
  link: IOnboardingLink;
  discount_prices?: IOnboardingDiscounts;
  subscription_details?: IOnboardingSubscriptionDetails;
}
